import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, Button, TextField, Tooltip } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';
import Autocomplete from '@material-ui/lab/Autocomplete';
import XLSX from 'xlsx';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Avoided from '../components/avoided';
import DateRange from '../../common/date-range';
import WorkdayRequest from '../components/workday-request';
import DownloadExcel from '../../common/download-excel';
import EmployeeHourEdit from '../components/employee-hour-edit';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { del, get, post } from '../../utils/api-services';
import themeColors from '../../assets/colors';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);

const useStyles = makeStyles((theme) => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 20
    },
    buttonsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    import: {
        ...themeColors.blueBtn
    },
    edit: {
        marginLeft: 20,
        ...themeColors.yellowBtn
    },
    delete: {
        marginLeft: 20,
        ...themeColors.redBtn
    },
    search: {
        marginLeft: 20,
        ...themeColors.greenBtn
    },
    tableContainer: {
        width: '100%',
        marginBottom: 20
    }
}));

const EmployeeHour = () => {

    const sede = JSON.parse(localStorage.getItem('@sede'));
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedHours, setSelectedHours] = useState([]);
    const [state, setState] = useState({
        loading: true,
        employees: [],
        data: [],
        filter: {
            startDate: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(2, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            employee: { id: 0, name: 'All employees' }
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(2, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        avoided: [],
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Employee hour';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/employeeHour', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, data: response.data, employees: response.employees }));
        } else {
            console.log('[EmployeeHour] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const search = async () => {
        const { filter } = state;

        showLoader(true);
        const response = await get('/employeeHour/search', filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response.data }));

            setExcelData([]);

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[EmployeeHour] search error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const destroy = async () => {
        const { startDate, endDate, employee } = state.filter;

        const cond = window.confirm(`This will delete data from ${startDate} to ${endDate} for ${employee.name}. \n\nThis action cannot be reversed. Do you want to continue?`);
        if (cond) {
            showLoader(true);
            const response = await del('/employeeHour', state.filter);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: [] }));
            } else {
                console.log('[EmployeeHour] desroy error', response);
                helpers.failRequest(response, showToast, signOut)
            }
            showLoader(false);
        }
    }

    const getToDownload = async () => {
        showLoader(true);
        const response = await get('/employeeHour/getToDownload', state.filter);
        if (response.status === 200) {
            setExcelData(response.data);

            if (!response?.data?.length) showToast('There is no data to download', 'warning');
        } else {
            console.log('[EmployeeHour] getToDownload error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const onCloseEdit = async () => {
        await search();
        setShowEdit(false)
    }

    const getDate = params => dayjs(params.value).format('MM/DD/YYYY');
    const getTime = params => dayjs(params.value).tz(sede.time_zone).format('h:mm A');
    const getName = params => params.value?.name ?? '?';
    const getActivity = ({ row }) => row?.cc2?.activity ?? '?';

    const columns = [
        { field: 'date', headerName: 'Date', flex: 0.8, minWidth: 100, valueGetter: getDate },
        { field: 'employee', headerName: 'Employee', flex: 2, minWidth: 220, valueGetter: getName },
        { field: 'actionRef', headerName: 'Action', flex: 1, minWidth: 130, valueGetter: getName },
        { field: 'start', headerName: 'Start', flex: 1, minWidth: 220, valueGetter: getTime },
        { field: 'stop', headerName: 'Stop', flex: 1, minWidth: 220, valueGetter: getTime },
        { field: 'hours', headerName: 'Hours', flex: 0.8, minWidth: 130 },
        { field: 'sede', headerName: 'Sede', flex: 0.8, minWidth: 100, valueGetter: getName },
        { field: 'activity', headerName: 'Activity', flex: 0.8, minWidth: 100, valueGetter: getActivity },
        { field: 'cc1', headerName: 'CC1', flex: 0.8, minWidth: 100, valueGetter: getName },
        { field: 'cc2', headerName: 'CC2', flex: 0.8, minWidth: 100, valueGetter: getName },
        { field: 'cc3', headerName: 'CC3', flex: 0.8, minWidth: 100, valueGetter: getName }
    ];

    const excelColumns = [
        { label: 'Sede code', value: 'sede.code' },
        { label: 'Sede name', value: 'sede.name' },
        { label: 'Date', value: 'date' },
        { label: 'Employee code', value: 'employee.code' },
        { label: 'Employee name', value: 'employee.name' },
        { label: 'Action', value: 'actionRef.name' },
        { label: 'Start', value: 'start' },
        { label: 'Stop', value: 'stop' },
        { label: 'Hours', value: 'hours' },
        { label: 'Paid hours', value: 'paid_hours' },
        { label: 'Unpaid hours', value: 'unpaid_hours' },
        { label: 'Regular hours', value: 'regular_hours' },
        { label: 'Overtime hours', value: 'overtime_week' },
        { label: 'Overtime holiday', value: 'overtime_holiday' },
        { label: 'Overtime holiday', value: 'overtime_holiday' },
        { label: 'CC1', value: 'cc1.name' },
        { label: 'CC2', value: 'cc2.name' },
        { label: 'CC2 activity', value: 'cc2.activity' },
        { label: 'CC3', value: 'cc3.name' }
    ];

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr) return <NotFound />;

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Employee hour' />

            <Grid container>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                type='text'
                                placeholder='Date range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Employee'}</FormLabel>
                        <Autocomplete
                            options={state.employees}
                            value={state.filter.employee}
                            getOptionLabel={employee => employee?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(event, value) => handleAutocomplete('employee', value)}
                            renderInput={params => <TextField {...params} placeholder='Employee' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonsContainer}>
                <DownloadExcel fileName='employee-hours-report' sheetName='Hours' data={excelData} columns={excelColumns} onClick={getToDownload} onReset={() => setExcelData([])} />

                {
                    state.permissions.delete_attr &&
                    <Tooltip title='This button will delete the data based on the fields above.' placement='top'>
                        <Button
                            variant='contained'
                            className={classes.delete}
                            onClick={destroy}
                        >
                            {'Delete'}
                        </Button>
                    </Tooltip>
                }
                {
                    state.permissions.update_attr &&
                    <Tooltip title='Changes will apply only to selected hours.' placement='top'>
                        <Button
                            variant='contained'
                            className={classes.edit}
                            onClick={() => setShowEdit(prev => !prev)}
                        >
                            {'Edit'}
                        </Button>
                    </Tooltip>
                }
                <Button
                    variant='contained'
                    className={classes.search}
                    onClick={search}
                >
                    {'Search'}
                </Button>
            </div>

            <EmployeeHourEdit open={showEdit} onClose={onCloseEdit} hours={selectedHours} permissions={state.permissions} />

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idemployee_hour}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={hours => setSelectedHours(hours)}
                />
            </div>

            <WorkdayRequest parent='hours' permissions={state.permissions} />

            <Avoided open={open} onClose={() => setOpen(false)} array={state.avoided} />

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />

        </div>
    );
}

export default EmployeeHour;